import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { MainLayout } from "./MainLayout";
import { AppShellContext } from "../../contexts/AppShellContext";
import { TimezoneChangeNotice } from "../common/TimezoneChangeNotice";
import { ContactChannelUpdatedModal } from "../common/ContactChannelUpdatedModal";
import { ComunidadLaunchModal } from "../common/ComunidadLaunchModal";
import { PsiChatWidget } from "../psychologist/home/PsiChatWidget";
import { Home, Calendar, Users, Users2, Map } from "lucide-react";
import { usePsychologistContext } from "../../contexts/PsychologistContext";
import { isImpersonated } from "../../hooks/api/auth/tokenManager";
import { redirectToN0Url } from "../../utils/n0Redirect";
import apiCoreAdapter from "../../hooks/apiCoreAdapter";
import psiPlatformAdapter from "../../hooks/psiPlatformAdapter";
import { getPsychologistMetrics } from "../../hooks/api/psychologist/metrics";
import { useNzJourneyStatus } from "../../hooks/useNzJourneyStatus";
import { NEW_STATUS_ID } from "../../utils/psychologistStatus";
import { calendarPath } from "../../routes/slugs";
import { isComunidadEnabled } from "../../utils/comunidadAccess";
import { useComunidadLaunch } from "../../hooks/useComunidadLaunch";
import { useActivityTracker } from "../../hooks/useActivityTracker";

type TabKey = "home" | "sesiones" | "agenda" | "camino-inicial" | "pacientes" | "comunidad" | "perfil" | "meet";

const TAB_CONFIG: { key: TabKey; path: string; label: string; icon: React.ReactNode }[] = [
  { key: "home", path: "/home", label: "Home", icon: <Home size={16} /> },
  { key: "agenda", path: calendarPath("week"), label: "Agenda", icon: <Calendar size={16} /> },
  { key: "pacientes", path: "/patients", label: "Pacientes", icon: <Users size={16} /> },
  { key: "comunidad", path: "/comunidad", label: "Comunidad", icon: <Users2 size={16} /> },
  { key: "camino-inicial", path: "/camino-inicial", label: "Camino Inicial", icon: <Map size={16} /> },
];

function homeBadgeSeenKey(psychologistId: string) {
  return `psi-home-nuevo-badge-seen-${psychologistId}`;
}

const pathToTab = (pathname: string): TabKey => {
  if (pathname.startsWith("/home")) return "home";
  if (pathname.startsWith("/sessions")) return "sesiones";
  if (pathname.startsWith("/calendar")) return "agenda";
  if (pathname.startsWith("/camino-inicial")) return "camino-inicial";
  if (pathname.startsWith("/patients")) return "pacientes";
  if (pathname.startsWith("/comunidad")) return "comunidad";
  if (pathname.startsWith("/profile")) return "perfil";
  // Videollamada embebida: no es ninguna pestaña (y no debe heredar el fondo ni la pestaña de Home)
  if (pathname.startsWith("/meet")) return "meet";
  return "home";
};

export const AppShell = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Métricas de Actividad: captura de pantallas visitadas/tiempo/acciones (no renderiza nada).
  useActivityTracker();
  const profileDirtyRef = useRef(false);
  const calendarDirtyRef = useRef(false);
  const { psychologist } = usePsychologistContext();
  const { canAccessHome, loading: nzJourneyLoading, egresoAck, journeyMode, journeyModeLoading } = useNzJourneyStatus();
  const showCaminoInicialTab = journeyMode !== 'none';

  // NZ recién creada, status 'new' (reemplaza a "pending discharged" para este momento del
  // ciclo de vida — cambio de backend/api-core). Mientras no cargó ningún horario de trabajo,
  // se queda adentro de Psi Platform pero restringida a Agenda únicamente (para que lo haga).
  // Una vez que carga horarios, si sigue sin haber tenido nunca un proceso, recién ahí se la
  // expulsa afuera de la plataforma (quierosumarme). Corre siempre que se resuelve
  // `psychologist.id` (no depende de haber visitado /calendar antes).
  const [restrictToAgenda, setRestrictToAgenda] = useState(false);
  const [onboardingGateLoading, setOnboardingGateLoading] = useState(true);
  // Se incrementa para forzar un re-chequeo del gate sin depender de un reload de pantalla
  // (ver listener de 'psi-work-schedule-saved' más abajo).
  const [onboardingGateRecheckToken, setOnboardingGateRecheckToken] = useState(0);

  useEffect(() => {
    // TEMP DEV OVERRIDE — comentado a pedido para poder testear este redirect impersonando
    // psicólogas (normalmente una sesión impersonada nunca se redirige, a propósito, para
    // poder soportar/debuggear otras cuentas sin que te expulse). Descomentar antes de commitear.
    // if (isImpersonated()) return;
    let cancelled = false;
    const id = psychologist?.id;
    if (!id) { setOnboardingGateLoading(false); return; }
    setOnboardingGateLoading(true);
    apiCoreAdapter.psychologistProfile().then(async profile => {
      if (cancelled) return;
      if (profile.ranking_category !== 'NZ' || profile.status_id !== NEW_STATUS_ID) {
        setRestrictToAgenda(false);
        return;
      }
      const [schedules, metrics] = await Promise.all([
        psiPlatformAdapter.psychologistWorkSchedules({ active: true, per_page: 1 }).catch(() => null),
        getPsychologistMetrics().catch(() => null),
      ]);
      if (cancelled) return;
      const hasWorkSchedules = (schedules?.results.length ?? 0) > 0;
      if (!hasWorkSchedules) {
        setRestrictToAgenda(true);
        return;
      }
      setRestrictToAgenda(false);
      const everHadProcesses = ((metrics?.active_processes ?? 0) + (metrics?.inactive_processes ?? 0)) > 0;
      if (!everHadProcesses) redirectToN0Url();
    }).catch(() => { /* noop */ }).finally(() => { if (!cancelled) setOnboardingGateLoading(false); });
    return () => { cancelled = true; };
  }, [psychologist?.id, onboardingGateRecheckToken]);

  // Al guardar un horario nuevo (WorkSchedulePanel), re-evaluar el gate de arriba sin
  // esperar a que la psicóloga recargue la pantalla — ver dispatch en
  // useCalendarScheduleOps.handlePanelSave.
  useEffect(() => {
    const handler = () => setOnboardingGateRecheckToken(t => t + 1);
    window.addEventListener('psi-work-schedule-saved', handler);
    return () => window.removeEventListener('psi-work-schedule-saved', handler);
  }, []);
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const [dirtySource, setDirtySource] = useState<'profile' | 'calendar' | null>(null);
  const [isCalendarTourRunning, setIsCalendarTourRunning] = useState(false);
  // Videollamada abierta (/meet/:token): se bloquea la navegación del encabezado para no salir sin querer en plena sesión
  const [isCallActive, setIsCallActive] = useState(false);
  const callActiveRef = useRef(false);
  callActiveRef.current = isCallActive;
  const navLocked = isCalendarTourRunning || isCallActive;

  const activeTab = pathToTab(location.pathname);

  // Mientras esté en el journey NZ (activa o recién graduada sin confirmar el popup de
  // egreso), no puede acceder a Home: se la manda a Agenda en vez de mostrarle /home.
  useEffect(() => {
    if (nzJourneyLoading) return;
    if (location.pathname.startsWith("/home") && !canAccessHome) {
      navigate(calendarPath("week"), { replace: true });
    }
  }, [nzJourneyLoading, canAccessHome, location.pathname, navigate]);

  // "Camino Inicial" es solo para NZ activa, en transición (O viniendo de NZ, pending-discharge)
  // o recién graduada sin confirmar egreso — a cualquier otra psicóloga que entre por URL
  // directa se la manda a Agenda, igual que el gate de Home de arriba.
  useEffect(() => {
    if (journeyModeLoading) return;
    if (location.pathname.startsWith("/camino-inicial") && !showCaminoInicialTab) {
      navigate(calendarPath("week"), { replace: true });
    }
  }, [journeyModeLoading, showCaminoInicialTab, location.pathname, navigate]);

  // NZ status 'new' sin horarios cargados: solo puede ver Agenda, cualquier otra ruta rebota
  // para acá (incluida /home, aunque ya la cubra el efecto de arriba).
  useEffect(() => {
    if (onboardingGateLoading) return;
    if (restrictToAgenda && !location.pathname.startsWith("/calendar")) {
      navigate(calendarPath("week"), { replace: true });
    }
  }, [onboardingGateLoading, restrictToAgenda, location.pathname, navigate]);

  // Badge "Nuevo" en el tab Home: solo para quien acaba de confirmar su egreso del journey
  // NZ, y solo hasta que entre a Home por primera vez.
  const [homeBadgeSeen, setHomeBadgeSeen] = useState<boolean>(() => {
    const id = psychologist?.id;
    return id ? localStorage.getItem(homeBadgeSeenKey(id)) === 'true' : true;
  });

  useEffect(() => {
    const id = psychologist?.id;
    if (!id) return;
    setHomeBadgeSeen(localStorage.getItem(homeBadgeSeenKey(id)) === 'true');
  }, [psychologist?.id]);

  useEffect(() => {
    const id = psychologist?.id;
    if (!id || homeBadgeSeen) return;
    if (canAccessHome && egresoAck && location.pathname.startsWith("/home")) {
      localStorage.setItem(homeBadgeSeenKey(id), 'true');
      setHomeBadgeSeen(true);
    }
  }, [canAccessHome, egresoAck, homeBadgeSeen, location.pathname, psychologist?.id]);

  const showHomeNuevoBadge = canAccessHome && egresoAck && !homeBadgeSeen;

  // Lanzamiento de Comunidad: pop-up único + badge "Nuevo" rojo en la pestaña. Se espera a que
  // se resuelvan los gates de arriba para no mostrarle el aviso a quien va a ser redirigida
  // (restrictToAgenda) ni mientras todavía no se sabe qué tabs le corresponden.
  const comunidadEnabled = isComunidadEnabled();
  const onComunidad = location.pathname.startsWith("/comunidad");
  const comunidadLaunch = useComunidadLaunch(
    comunidadEnabled && !onboardingGateLoading && !restrictToAgenda && !nzJourneyLoading && !journeyModeLoading,
    psychologist?.id,
  );
  const { markVisited: markComunidadVisited } = comunidadLaunch;

  // Entrar a Comunidad apaga el badge "Nuevo" para siempre.
  useEffect(() => {
    if (onComunidad && comunidadLaunch.showBadge) markComunidadVisited();
  }, [onComunidad, comunidadLaunch.showBadge, markComunidadVisited]);

  const visibleTabConfig = useMemo(() => {
    // Mientras no sepamos con certeza qué tabs corresponden, no mostramos ninguno — evita que
    // Agenda aparezca sola y Pacientes/Home se sumen unos segundos después (flash de tabs
    // apareciendo de a uno mientras se resuelven los chequeos en paralelo).
    if (onboardingGateLoading || nzJourneyLoading || journeyModeLoading) {
      return [];
    }
    // "Comunidad" aparece en la nav según `isComunidadEnabled()` (hoy abierta a todas; ver
    // ComunidadGuard, que además bloquea la ruta si se vuelve a cerrar).
    const base = isComunidadEnabled() ? TAB_CONFIG : TAB_CONFIG.filter((t) => t.key !== "comunidad");
    if (restrictToAgenda) {
      return base.filter((t) => t.key === "agenda");
    }
    const withHome = canAccessHome ? base : base.filter((t) => t.key !== "home");
    return showCaminoInicialTab ? withHome : withHome.filter((t) => t.key !== "camino-inicial");
  }, [onboardingGateLoading, restrictToAgenda, nzJourneyLoading, canAccessHome, journeyModeLoading, showCaminoInicialTab]);

  const setProfileDirty = useCallback((dirty: boolean) => {
    profileDirtyRef.current = dirty;
  }, []);

  const setCalendarDirty = useCallback((dirty: boolean) => {
    calendarDirtyRef.current = dirty;
  }, []);

  const safeNavigate = useCallback((path: string) => {
    if (callActiveRef.current) return;
    if (location.pathname.startsWith("/profile") && profileDirtyRef.current) {
      setDirtySource('profile');
      setPendingPath(path);
      return;
    }
    if (location.pathname.startsWith("/calendar") && calendarDirtyRef.current) {
      setDirtySource('calendar');
      setPendingPath(path);
      return;
    }
    navigate(path);
  }, [location.pathname, navigate]);

  const noPadding = activeTab === "agenda" || activeTab === "pacientes" || activeTab === "comunidad" || activeTab === "sesiones" || activeTab === "home" || activeTab === "camino-inicial" || activeTab === "meet"
    || location.pathname.startsWith("/patients/") || location.pathname.startsWith("/sessions/") || location.pathname.startsWith("/calendar/")
    || location.pathname === "/profile/farewell";

  const contextValue = useMemo(() => ({
    setProfileDirty,
    setCalendarTourRunning: setIsCalendarTourRunning,
    setCalendarDirty,
    setCallActive: setIsCallActive,
  }), [setProfileDirty, setCalendarDirty]);

  const TabButton = ({
    tabKey,
    label,
    icon,
    path,
    showBadge,
    badgeTone = "green",
  }: {
    tabKey: TabKey;
    label: string;
    icon: React.ReactNode;
    path: string;
    showBadge?: boolean;
    badgeTone?: "green" | "red";
  }) => (
    <button
      type="button"
      className={`tab-button ${activeTab === tabKey ? "active" : ""}`}
      onClick={() => safeNavigate(path)}
      disabled={navLocked}
      style={{
        cursor: navLocked ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        opacity: navLocked ? 0.5 : 1,
        pointerEvents: navLocked ? "none" : "auto",
      }}
    >
      {icon}
      {label}
      {showBadge && (
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold leading-none text-white ${badgeTone === "red" ? "bg-red-500" : "bg-emerald-500"}`}>
          Nuevo
        </span>
      )}
    </button>
  );

  return (
    <AppShellContext.Provider value={contextValue}>
      <MainLayout
        onProfileClick={() => safeNavigate("/profile")}
        showProfileButton={!onboardingGateLoading && !restrictToAgenda}
        noPadding={noPadding}
        contentBackground={activeTab === "home" ? "#423568" : undefined}
        isCalendarTourRunning={navLocked}
        navigation={
          <>
            {visibleTabConfig.map((t) => (
              <TabButton
                key={t.key}
                tabKey={t.key}
                label={t.label}
                icon={t.icon}
                path={t.path}
                showBadge={(t.key === "home" && showHomeNuevoBadge) || (t.key === "comunidad" && comunidadLaunch.showBadge)}
                badgeTone={t.key === "comunidad" ? "red" : "green"}
              />
            ))}
          </>
        }
      >
        <Outlet />
      </MainLayout>

      {/* Auto-aviso de huso: SOLO para psicólogas reales (una sesión impersonada
          no debe cambiarle el huso a la psicóloga desde este aviso). */}
      {!isImpersonated() && !location.pathname.startsWith('/profile') && <TimezoneChangeNotice key={location.pathname} />}

      {/* Aviso único del canal de contacto nuevo. A diferencia del resto de los avisos,
          SÍ se muestra impersonado a propósito (para poder testearlo) — el "Entendido"
          solo marca localStorage del navegador que impersona, no le toca nada a la
          psicóloga real. */}
      <ContactChannelUpdatedModal />

      {/* Pop-up único de lanzamiento de Comunidad. Impersonado SÍ se muestra (para poder
          testearlo) pero lo que se haga queda solo en el localStorage de ese navegador, nunca
          en el backend — ver useComunidadLaunch. Ya estando en Comunidad no tiene sentido
          avisarle de Comunidad, así que ahí no sale. */}
      <ComunidadLaunchModal
        open={comunidadLaunch.showModal && !onComunidad}
        onLearnMore={() => {
          comunidadLaunch.dismiss();
          safeNavigate("/comunidad?destacar=formaciones");
        }}
        onClose={comunidadLaunch.dismiss}
        onSnooze={comunidadLaunch.snooze}
      />

      {/* Bot flotante — visible en todas las pantallas, incluida impersonación */}
      {/* Mismo gate que Home (`canAccessHome`, ver useNzJourneyStatus): mientras esté en el
          journey NZ no tiene sentido mostrarle el bot, que hoy responde con la KB completa de
          Home. En su lugar tiene el JourneyBot dentro de "Tu camino inicial" (Perfil ->
          Métricas), que sí está pensado para esa etapa. */}
      {/* En /meet/:token no va: se pisa con la videollamada */}
      {!onboardingGateLoading && !restrictToAgenda && !nzJourneyLoading && canAccessHome && activeTab !== "meet" && (
        <PsiChatWidget onNavigate={safeNavigate} />
      )}

      {/* Unsaved changes confirmation modal (profile or calendar) */}
      {pendingPath && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ background: '#fff', borderRadius: '1.5rem', padding: '2rem', maxWidth: 400, width: '90%', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151', marginBottom: '1rem' }}>Cambios sin guardar</h3>
            <p style={{ fontSize: '1rem', color: '#6b7280', marginBottom: '2rem', lineHeight: 1.5 }}>
              Tenés cambios sin guardar en {dirtySource === 'calendar' ? 'tu configuración de horarios' : 'tu perfil'}. Si salís ahora, se perderán.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <button
                type="button"
                style={{ background: '#6D57BC', color: '#fff', border: 'none', borderRadius: 9999, padding: '0.75rem 1.25rem', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer' }}
                onClick={() => { setPendingPath(null); setDirtySource(null); }}
              >
                Seguir editando
              </button>
              <button
                type="button"
                style={{ background: '#fff', color: '#ef4444', border: '2px solid #ef4444', borderRadius: 9999, padding: '0.75rem 1.25rem', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer' }}
                onClick={() => {
                  if (dirtySource === 'calendar') {
                    calendarDirtyRef.current = false;
                  } else {
                    profileDirtyRef.current = false;
                  }
                  setDirtySource(null);
                  navigate(pendingPath);
                  setPendingPath(null);
                }}
              >
                Salir sin guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShellContext.Provider>
  );
};
